import React from "react";
import { OPTIONS_OFF_GLYPH_PATH, OPTIONS_OFF_GLYPH_VIEWBOX } from "../options-off-glyph";
import { OPTIONS_ON_GLYPH_PATH, OPTIONS_ON_GLYPH_VIEWBOX } from "../options-on-glyph";
import styles from "./text-field.module.css";
import { withSafeRender } from "../safe-render";

/**
 * HelperMessage — the line under a field, matching Eclipse "Helper message"
 * (`3564bb5cc8a6cbbfdc7694cab56fd5fbdd343c9d`). Kept inside `text-field/` rather
 * than its own atom folder: nothing outside a labelled field composes it today,
 * and Dropdown (`atoms/dropdown`) imports it from here rather than this tree
 * growing a second copy — if a third control ever needs it this should move up
 * to its own atom, but two call sites importing one file is not that yet.
 *
 * No state, no handlers, so it carries no `"use client"` of its own — but both
 * call sites (`text-field.client.tsx`, `dropdown.client.tsx`) do, so in practice it always
 * ships as client JavaScript. It is only a Server Component for a host that
 * composes it directly, which nothing does today.
 */
export interface HelperMessageProps {
  /** Ties the message to its field via the field's `aria-describedby`. */
  id: string;
  /** `"error"` gets `role="alert"` so assistive tech announces it the moment it
   *  appears; `"helper"` is read only when the reader reaches it. `"success"` (Figma
   *  10188:18315) is green with a check; it takes no role, so a host announcing it
   *  wraps it in its own `role="status"` region. */
  tone?: "helper" | "error" | "success" | "disabled";
  /** Set false when focus moves to the field, whose `aria-describedby` already reads the error. Defaults to true. */
  announce?: boolean;
  children: React.ReactNode;
  className?: string;
}

function HelperMessageImpl({ id, tone = "helper", announce = true, children, className }: HelperMessageProps) {
  const hasContent = !(children === undefined || children === null || children === "");

  if (!hasContent) {
    return null;
  }

  const isError = tone === "error";
  const isSuccess = tone === "success";

  return (
    <p
      className={[
        styles.helperMessage,
        isError ? styles.helperMessageError : null,
        isSuccess ? styles.helperMessageSuccess : null,
        tone === "disabled" ? styles.helperMessageDisabled : null,
        className
      ]
        .filter(Boolean)
        .join(" ")}
      id={id}
      role={isError && announce ? "alert" : undefined}
    >
      {isError || isSuccess ? (
        <>
          <svg
            aria-hidden="true"
            className={styles.helperIcon}
            focusable="false"
            viewBox={isError ? OPTIONS_OFF_GLYPH_VIEWBOX : OPTIONS_ON_GLYPH_VIEWBOX}
          >
            <path d={isError ? OPTIONS_OFF_GLYPH_PATH : OPTIONS_ON_GLYPH_PATH} fill="currentColor" />
          </svg>
          <span className={styles.helperText}>{children}</span>
        </>
      ) : children}
    </p>
  );
}
export const HelperMessage = withSafeRender(HelperMessageImpl, "HelperMessage");

export default HelperMessage;
